import { useState } from "react";
import {
  Building2,
  Users,
  DollarSign,
  Calendar,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  TrendingUp,
  MapPin,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { cn } from "../../../../lib/utils";

const departmentIcons = {
  "Ressources Humaines": {
    icon: UserCheck,
    color: "text-blue-600",
    bg: "bg-blue-100",
  },
  Informatique: {
    icon: Building2,
    color: "text-purple-600",
    bg: "bg-purple-100",
  },
  Marketing: { icon: TrendingUp, color: "text-green-600", bg: "bg-green-100" },
  Commercial: {
    icon: DollarSign,
    color: "text-orange-600",
    bg: "bg-orange-100",
  },
  Production: { icon: Building2, color: "text-gray-600", bg: "bg-gray-100" },
  default: { icon: Building2, color: "text-blue-600", bg: "bg-blue-100" },
};

export default function DepartmentCard({
  department,
  onView,
  onEdit,
  onDelete,
  onViewEmployees,
  className,
}) {
  const [isHovered, setIsHovered] = useState(false);

  const formatCurrency = (amount) => {
    if (!amount) return "Non défini";
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "Non défini";
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      notation: numericAmount > 1000000 ? "compact" : "standard",
    }).format(numericAmount);
  };

  const formatDate = (date) => {
    if (!date) return "Non défini";
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  const getDepartmentIcon = (name) => {
    const config = departmentIcons[name] || departmentIcons.default;
    return config;
  };

  const iconConfig = getDepartmentIcon(department.nom);
  const IconComponent = iconConfig.icon;

  const budgetUtilization =
    department.budget && department._count?.employes
      ? Math.min(
          ((department._count.employes * 50000) /
            parseFloat(department.budget)) *
            100,
          100
        )
      : 0;

  return (
    <Card
      className={cn(
        "group relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1",
        "border border-gray-200 hover:border-gray-300",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50/30 pointer-events-none" />

      <CardHeader className="pb-3 relative">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={cn("p-2 rounded-lg", iconConfig.bg)}>
              <IconComponent className={cn("h-5 w-5", iconConfig.color)} />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg truncate group-hover:text-blue-600 transition-colors">
                {department.nom}
              </CardTitle>
              {department.description && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {department.description}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Badge
              variant={department.actif ? "default" : "secondary"}
              className={cn(
                "transition-all duration-200",
                department.actif
                  ? "bg-green-100 text-green-800 hover:bg-green-200"
                  : "bg-gray-100 text-gray-600"
              )}
            >
              {department.actif ? "Actif" : "Inactif"}
            </Badge>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                    isHovered && "opacity-100"
                  )}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onView(department)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Voir détails
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewEmployees(department)}>
                  <Users className="h-4 w-4 mr-2" />
                  Voir employés
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onEdit(department)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDelete(department, false)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {department.actif ? "Désactiver" : "Supprimer"}
                </DropdownMenuItem>
                {!department.actif && (
                  <DropdownMenuItem
                    onClick={() => onDelete(department, true)}
                    className="text-red-800 focus:text-red-800"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer définitivement
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 relative">
        {/* Manager Section */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Manager:</span>
          <div className="flex items-center space-x-2">
            {department.manager ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs bg-blue-100 text-blue-600">
                    {department.manager.prenom?.[0]}
                    {department.manager.nom?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium truncate max-w-32">
                  {department.manager.prenom} {department.manager.nom}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-400 italic">Non assigné</span>
            )}
          </div>
        </div>

        {/* Employees Section */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Employés:</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewEmployees(department)}
            className="h-auto p-1 hover:bg-blue-50"
          >
            <Badge
              variant="secondary"
              className="bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
            >
              <Users className="h-3 w-3 mr-1" />
              {department._count?.employes || 0}
            </Badge>
          </Button>
        </div>

        {/* Budget Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Budget:</span>
            <span className="text-sm font-medium">
              {formatCurrency(department.budget)}
            </span>
          </div>

          {/* Budget Utilization Bar */}
          {department.budget && budgetUtilization > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>Utilisation estimée</span>
                <span>{budgetUtilization.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-500",
                    budgetUtilization > 80
                      ? "bg-red-500"
                      : budgetUtilization > 60
                      ? "bg-yellow-500"
                      : "bg-green-500"
                  )}
                  style={{ width: `${budgetUtilization}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Creation Date */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            <span>Créé le {formatDate(department.date_creation)}</span>
          </div>

          {/* Quick Action Buttons */}
          <div
            className={cn(
              "flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200",
              isHovered && "opacity-100"
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(department)}
              className="h-6 w-6 p-0"
              title="Voir détails"
            >
              <Eye className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewEmployees(department)}
              className="h-6 w-6 p-0"
              title="Voir employés"
            >
              <Users className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Hover Effect Border */}
      <div
        className={cn(
          "absolute inset-0 border-2 border-transparent rounded-lg pointer-events-none transition-colors duration-200",
          isHovered && "border-blue-200"
        )}
      />
    </Card>
  );
}
